import { DlpInfo, FileResponseInterface, ScanResults } from 'services/interface';
import { getMultiscanVerdict, getScanVerdict, hasMetascanRun } from 'utils/verdict/Verdict';

export { hasMetascanRun, isMetascanPartial } from 'utils/verdict/Verdict';

// The predictive engine key in scan_details may arrive under either name.
export const PREDICTIVE_ENGINE_NAMES = ['OPSWAT AI-Predictive Malware Detection', 'OPSWAT Predictive Alin AI'];

// True only when the Predictive AI engine itself flagged the file — not when other AV engines did.
// Shared by the Overview card and the Threats Detected header badge so the two never disagree.
export const isPredictiveAiDetected = (fileData?: FileResponseInterface): boolean => {
    const scanResults = fileData?.scan_results;
    const detail = PREDICTIVE_ENGINE_NAMES.map((name) => scanResults?.scan_details?.[name]).find(Boolean);
    if (detail) {
        // scan_result_i 23 = engine ran but abstained ("Not Supported"), so it stays not detected.
        return detail.scan_result_i !== 23 && getScanVerdict(detail.scan_result_i)?.variant === 'alert';
    }
    // MDCI-1561: a Blocked scan can name OPSWAT AI only in the aggregate result, with scan_details empty.
    if (fileData?.process_info?.result !== 'Blocked') {
        return false;
    }
    const reasons = [scanResults?.scan_all_result_a, ...(fileData.process_info.verdicts ?? [])];
    return reasons.some((reason) => {
        const text = reason?.toLowerCase() ?? '';
        return text.includes('opswat ai') && text.includes('malicious');
    });
};

// The platform's `total_detected_avs` only counts Infected/Suspicious engines: an engine returning
// 22 (Potentially Unwanted Program) leaves it at 0 even though the engine did flag the file, so the
// Overview card and the Metascan header claimed "No Threats Detected" while the Threats table drew
// red rows for those engines (MDCL-18093). Derive the count from scan_details with the same
// getScanVerdict mapping the table uses, so the count and the rows agree by construction, and never
// go below the backend's number.
export const countDetectedEngines = (scanResults?: ScanResults): number => {
    const details = scanResults?.scan_details;
    const fromDetails = details
        ? Object.values(details).filter((detail) => getScanVerdict(detail?.scan_result_i)?.label === 'Detected').length
        : 0;
    return Math.max(fromDetails, scanResults?.total_detected_avs ?? 0);
};

// A clean headline needs an analysis behind it. Every technology has a way of not finishing — engines
// that aborted, failed or refused the type, a sandbox that gave up, a sanitization that errored — and
// when none of them finished, "No Threats Detected" is fail-open: nothing ever looked at the bytes
// (MDCI-976). Each gate mirrors the matching Overview card's active / failed check, so the header only
// claims a verdict a card below can back up. `sandboxVerdict` is whichever verdict string the caller
// resolved (filescan or report); Unknown is the sandbox declining to answer, not an answer.
export const hasCompletedAnalysis = (fileData?: FileResponseInterface, sandboxVerdict?: string): boolean => {
    const scanResults = fileData?.scan_results;
    const metascanFinished = hasMetascanRun(scanResults) && getMultiscanVerdict(scanResults).label !== 'Failed';
    const sandboxAnswered = Boolean(sandboxVerdict) && sandboxVerdict?.toLowerCase() !== 'unknown';
    const cdrRan = Boolean(fileData?.process_info?.post_processing?.sanitization_details || fileData?.sanitized);
    const cdrFinished = cdrRan && fileData?.sanitized?.result !== 'Error';
    // DLP verdict codes 3 (Failed) and 4 (Not Supported) — see verdictDLP.
    const dlpCode = fileData?.dlp_info?.verdict;
    const dlpFinished = Boolean(fileData?.dlp_info) && dlpCode !== 3 && dlpCode !== 4;
    const aigcdFinished = Boolean(fileData?.aigcd_info?.final_verdict?.verdict);
    return metascanFinished || sandboxAnswered || cdrFinished || dlpFinished || aigcdFinished;
};

export const sumSanitizedObjectCounts = (fileData?: FileResponseInterface): number => {
    const rows = fileData?.process_info?.post_processing?.sanitization_details?.details;
    if (!rows || rows.length === 0) {
        return 0;
    }

    let total = 0;
    for (const row of rows) {
        const count = typeof row.count === 'number' ? row.count : Number.parseInt(String(row.count), 10);
        total += Number.isNaN(count) ? 0 : count;
    }
    return total;
};

export const sumDlpHitRows = (dlp?: DlpInfo): number => {
    if (!dlp?.hits) {
        return 0;
    }

    let total = 0;
    const categories = Object.values(dlp.hits);
    for (const category of categories) {
        if (category?.hits?.length !== undefined) {
            total += category.hits.length;
        }
    }
    return total;
};

/**
 * An Aether scan (MDCI-919). The workflow name is on the first response (`process_info.profile`), the
 * engine blocks (`reputation_info`, `threatintelligence_info`) land as each stage completes — either
 * one says Aether, so the cards and the tab show from the first frame instead of popping in later.
 */
export const isAetherResult = (
    fileData?: Partial<Pick<FileResponseInterface, 'process_info' | 'reputation_info' | 'threatintelligence_info'>>,
): boolean =>
    Boolean(
        fileData?.process_info?.profile?.toLowerCase().startsWith('aether') ||
            fileData?.reputation_info ||
            fileData?.threatintelligence_info,
    );
